import PropTypes from "prop-types";
import { Tooltip } from "antd";
import { CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { BadgeRoot, BadgeGlyph } from "./AIStatusBadgeStyled";

/**
 * Inline AI-status indicator for the matter document table (and any other
 * doc-list surface that wants to flag running / completed / failed deep
 * analysis at a glance).
 *
 * The component is *strictly* derived state — it owns no fetch or polling.
 * Callers pass the backend-surfaced field:
 *
 *   - `analysisState`  (string|null) — most-recent `AnalysisJob` status:
 *                                       `null | "running" | "completed" | "failed"`
 *
 * Until the matter document list serializer ships that field the badge
 * renders nothing — the surface degrades silently rather than showing a
 * misleading "no AI" signal across the table. That null-safe default is
 * what unblocks shipping the FE without waiting on the BE field
 * addition.
 *
 * Variant precedence (highest → lowest):
 *   1. analysis_state === "running"   — active signal beats everything
 *   2. analysis_state === "completed" — finished deep analysis
 *   3. analysis_state === "failed"    — finished but bad
 *
 * The ordering matches what an attorney triages first: jobs in flight
 * before jobs that finished, and finished-good before finished-bad would
 * be wrong (failures need eyes), so failed reads above completed in
 * tooltip but ranks below running because running is more actionable
 * (you might cancel) and completed/failed are terminal.
 *
 * History: a fourth `briefing` variant existed pre-2026-04-29, sourced
 * from a `hasBriefing` prop that mirrored `Document.has_briefing` on the
 * backend. It was dropped in the same change that killed the standalone
 * "Briefing" tab from matter mode (renamed Analysis → "The Brief"); the
 * variant no longer had a click destination because the badge is only
 * surfaced in matter mode. The auto-briefing artifact still exists
 * server-side and is still shown in the inbox surface — the badge just
 * doesn't advertise its presence in the matter document table.
 */

const TOOLTIP_BY_VARIANT = {
  running: "Deep analysis in progress",
  completed: "Deep analysis complete",
  failed: "Deep analysis failed",
};

const ARIA_LABEL_BY_VARIANT = {
  running: "Deep analysis in progress",
  completed: "Deep analysis complete",
  failed: "Deep analysis failed",
};

/**
 * Map analysisState → variant key.
 * Returns null when there is no AI signal to render.
 *
 * Exported (and unit-tested) so the precedence rule has a single
 * authoritative implementation; consumers that want to reason about the
 * variant without rendering (e.g. row-level analytics, inbox count
 * rollups) can import this directly.
 *
 * Accepts a destructured object purely for forward-compatibility with
 * future fields (e.g. a hypothetical `chatActivity` signal); pass
 * `{ analysisState }` and ignore the rest.
 */
export const resolveAIStatusVariant = ({ analysisState } = {}) => {
  if (analysisState === "running") return "running";
  if (analysisState === "completed") return "completed";
  if (analysisState === "failed") return "failed";
  return null;
};

const AIStatusBadge = ({
  analysisState = null,
  onActivate,
  className,
}) => {
  const variant = resolveAIStatusVariant({ analysisState });
  if (!variant) return null;

  const interactive = typeof onActivate === "function";
  const handleClick = interactive
    ? (e) => {
        e.stopPropagation();
        onActivate({ variant });
      }
    : undefined;
  const handleKeyDown = interactive
    ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onActivate({ variant });
        }
      }
    : undefined;

  let glyph = null;
  if (variant === "completed") {
    glyph = (
      <BadgeGlyph>
        <CheckCircle size={10} weight="fill" aria-hidden="true" />
      </BadgeGlyph>
    );
  } else if (variant === "failed") {
    glyph = (
      <BadgeGlyph>
        <WarningCircle size={10} weight="fill" aria-hidden="true" />
      </BadgeGlyph>
    );
  }

  return (
    <Tooltip title={TOOLTIP_BY_VARIANT[variant]} placement="top">
      <BadgeRoot
        $variant={variant}
        $interactive={interactive}
        className={className}
        aria-label={ARIA_LABEL_BY_VARIANT[variant]}
        role={interactive ? "button" : "img"}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        data-testid={`ai-status-badge-${variant}`}
      >
        {glyph}
      </BadgeRoot>
    </Tooltip>
  );
};

AIStatusBadge.propTypes = {
  analysisState: PropTypes.oneOf(["running", "completed", "failed", null]),
  /**
   * Optional click handler. When provided the badge becomes a focusable
   * `button` and forwards `{ variant }` so callers can route the row
   * open + AI tab selection. All variants currently route to the
   * `analysis` tab (rendered as "The Brief" in matter mode). Visual-only
   * when omitted (Phase 6 v1).
   */
  onActivate: PropTypes.func,
  className: PropTypes.string,
};

export default AIStatusBadge;
